
import Clarity from '@microsoft/clarity';
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { CLARITY_PROJECT_ID } from '../../../project_config';
import { api } from "../../../utils/api";
import { LoginHelper } from "../../../utils/LoginHelper";
import Breadcrumb from "../../common/breadcrumb/Breadcrumb";
import Footer from "../../common/footer/Footer";
import { FooterButton } from "../../common/footer/FooterButton";
import Header from "../../common/header/Header";
import Loading from "../../common/loading/Loading";
import SeoMeta from "../../common/seo/SeoMeta";
import {
	buildCanonicalUrl,
	publicationMetaDescription,
	SITE_NAME,
} from "../../common/seo/seoUtils";
import DetailAdminManagementPanel from "../../common/detail/DetailAdminManagementPanel";
import {
	canShowDetailManagement,
	isSiteAdmin,
} from "../../../utils/detailManagementAccess";
import ActionSection from "./action/ActionSection";
import ContentSection from "./content/ContentSection";
import PublicationSummarySection from "./summary/PublicationSummarySection";

export default function PublicationDetail() {
	Clarity.init(CLARITY_PROJECT_ID);
	const [isDesktop, setIsDesktop] = useState(() => window.innerWidth > 768);
	const params = useParams();
	const publicationId = params.publicationId;
	const [publication, setPublication] = useState(null);
	const [errorMessage, setErrorMessage] = useState('');
	const [isLoading, setIsLoading] = useState(true);

	const linkDataList = [
		{ label: 'خانه', href: '/' },
		{ label: 'مقالات', href: '/publication_list/' },
		{ label: publication?.title || 'مقاله' },
	];

	useEffect(() => {
		const onResize = () => setIsDesktop(window.innerWidth > 768);
		window.addEventListener("resize", onResize);
		return () => window.removeEventListener("resize", onResize);
	}, []);

	useEffect(() => {
		setIsLoading(true);
		api.publicationDetail({
			token: LoginHelper.getToken(),
			publication_id: publicationId,
		}).then((result) => {
			setPublication(result);
			setIsLoading(false);
		}).catch((err) => {
			setIsLoading(false);
			if (err && err.data) {
				setErrorMessage(err.data.message);
			}
		});
	}, [publicationId]);

	const seoDescription = useMemo(
		() => publicationMetaDescription(publication),
		[publication],
	);

	const articleJsonLd = useMemo(() => {
		if (!publication?.title) return null;
		return {
			"@context": "https://schema.org",
			"@type": "Article",
			headline: publication.title,
			description: seoDescription,
			image: publication.image_url ? [publication.image_url] : undefined,
			datePublished: publication.create_datetime || undefined,
			author: publication.profile_detail?.name
				? {
						"@type": "Person",
						name: publication.profile_detail.name,
					}
				: undefined,
			publisher: {
				"@type": "Organization",
				name: SITE_NAME,
			},
			mainEntityOfPage: buildCanonicalUrl(
				`/publication_detail/${publication.id}/`,
			),
		};
	}, [publication, seoDescription]);

	const isOwner = Boolean(publication?.is_owner);
	const showManagement = publication && canShowDetailManagement(isOwner);

	const sidebarBlocks = showManagement ? (
		isOwner ? (
			<ActionSection publication={publication} />
		) : (
			isSiteAdmin() && (
				<DetailAdminManagementPanel
					ariaLabel="مدیریت مقاله"
					editHref={`/publication_edit/${publicationId}/`}
					dashboardHref="/dashboard/"
					dashboardLabel="داشبورد مدیریت"
				/>
			)
		)
	) : null;

	return (
		<>
			<SeoMeta
				title={publication?.title ? `مقاله ${publication.title}` : "مقاله"}
				description={seoDescription}
				path={`/publication_detail/${publicationId}/`}
				imageUrl={publication?.image_url}
				type="article"
				jsonLd={articleJsonLd}
			/>

			<div className="bg-[#F0F6FC] min-h-screen flex flex-col justify-between">
				<div>
					<Header isDesktop={isDesktop} />
					<div className="bg-white">
						<div className="mx-auto w-full max-w-[1200px] px-[10px] md:px-4">
							<Breadcrumb linkDataList={linkDataList} />
						</div>
					</div>

					<div className="mx-auto flex w-full min-w-0 max-w-[1200px] flex-col gap-5 overflow-x-hidden px-[10px] pb-8 pt-6 md:px-4 md:pt-8">
						{isLoading && (
							<div className="flex justify-center py-16">
								<Loading color="#ff694b" width={44} />
							</div>
						)}

						{errorMessage && !publication && (
							<div className="rounded-[14px] border border-red-200 bg-white px-4 py-6 text-center font-Vazir-400 text-[#cc0000]">
								{errorMessage}
							</div>
						)}

						{publication && (
							isDesktop ? (
								<div className="flex flex-row items-start gap-5">
									<div className="flex min-w-0 flex-1 flex-col gap-5">
										<PublicationSummarySection publication={publication} />
										<ContentSection publication={publication} />
									</div>
									{showManagement && (
										<aside className="sticky top-4 flex w-[300px] shrink-0 flex-col gap-4">
											{sidebarBlocks}
										</aside>
									)}
								</div>
							) : (
								<div className="flex flex-col gap-5">
									<PublicationSummarySection publication={publication} />
									{showManagement && sidebarBlocks}
									<ContentSection publication={publication} />
								</div>
							)
						)}
					</div>
				</div>
				<Footer isDesktop={isDesktop} activeButton={FooterButton.PUBLICATIONS} />
			</div>
		</>
	);
}
